import App from 'components/app';
import { appVersion, appVersionHash, isDebug, isDevNodeEnv, sentryDSN, stage } from 'constants/config';
import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { addUserSnapshotContextToEvent, parseUrlForFingerprint, updateFingerprint } from 'utils/sentry.utils';
import throttle from 'lodash/throttle';
import { setLogger as setRetryLogger } from '@env0/common-retry';
import { setUseWhatChange } from '@simbathesailor/use-what-changed';
import { startMocks } from 'utils/msw';
import { syncDocumentThemeFromStorage } from 'components/theme-provider-wrapper';

// ORDER IS IMPORTANT
import 'antd/es/style/reset.css';
import '@env0/ui-theme/globals.css';
import { AxiosError } from 'axios';

Object.assign(window, { env0FrontendVersion: appVersion, env0FrontendVersionHash: appVersionHash });

// Loading New Relic and Appcues
declare global {
  interface Window {
    NREUM: any;
    Appcues: any;
    nonce: string;
    cspScriptStarted?: boolean;
    cspScriptFinished?: boolean;
    cspScriptInitialized?: boolean;
  }
}

const UPLOAD_SCREENSHOT_THROTTLE = 5 * 60 * 1000; // 5 minutes
const throttledAddUserSnapshotContextToEvent = throttle(addUserSnapshotContextToEvent, UPLOAD_SCREENSHOT_THROTTLE);

if (sentryDSN) {
  Sentry.init({
    dsn: sentryDSN,
    environment: stage,
    release: `env0@${appVersion}@${appVersionHash}`,
    integrations: [
      Sentry.captureConsoleIntegration(),
      Sentry.contextLinesIntegration(),
      Sentry.extraErrorDataIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration()
    ],
    // Since v9 the SDK sends the user's IP only with this on; auth, token and cookie values stay [Filtered].
    sendDefaultPii: true,
    normalizeDepth: 5,
    beforeSend: async function (event, hint) {
      addRequestUrlCustomTag(event, hint);

      // NOTE: avoid console.log here, as long as CaptureConsole is enabled, it will cause infinite loop
      updateFingerprint(event, hint);
      if (event.level === 'error' || event.level === 'fatal') {
        await throttledAddUserSnapshotContextToEvent(event);
      }

      return event;
    },
    tracesSampleRate: 0.5,
    profilesSampleRate: 0.3,
    ignoreErrors: ["Failed to read the 'cssRules' property from 'CSSStyleSheet'"]
  });
}

// global logger to retry function so whoever uses it will have logs
setRetryLogger({ info: console.info, warning: console.warn, debug: console.debug });

async function main() {
  const isCypress = !!(window as { Cypress?: any }).Cypress;
  if (isDebug && !isCypress) {
    setUseWhatChange(isDevNodeEnv);
    const whyDidYouRender = await import('@welldone-software/why-did-you-render');
    whyDidYouRender.default(React);
  }

  await startMocks();

  // Before the first paint, so the CSS variables match the theme emotion is about to render with.
  syncDocumentThemeFromStorage();

  createRoot(document.getElementById('root') as HTMLElement).render(<App />);
}

main().catch(console.error);

const addRequestUrlCustomTag = (event: Sentry.ErrorEvent, hint: Sentry.EventHint) => {
  const originalException = hint.originalException;
  let url: string | undefined;

  if (originalException instanceof AxiosError && originalException?.config?.url) {
    url = originalException.config.url;
  } else if (event.exception?.values?.[0]?.mechanism?.type === 'http.client') {
    url = event.request?.url;
  }

  if (url) {
    Sentry.withScope(scope => {
      scope.setTag('request.url', parseUrlForFingerprint(url));
    });
  }
};
